/**
 * useWarningAcknowledgements — the live half of warning acknowledgement
 * (2026-10-06): ONE hook for ora's drawer, ora's run record and the Outlook
 * add-in's drawer (one UI standard).
 *
 * It holds what the person did on THIS screen on top of what the server last
 * said, and it never claims more than the server confirmed (Ruling R50):
 *   - a press marks the line PENDING ("Acknowledging…") — not acknowledged;
 *   - only the server's answer acknowledges it, and when somebody else got there
 *     first (two people at once) the line names THEM;
 *   - a failed request clears the pending mark and says so — a failure never
 *     reads as done, and "Completed" without the "*" needs every line confirmed;
 *   - `needsAcknowledgement` is re-derived from the lines, never copied off the
 *     payload. An empty list `[]` is false (a server-confirmed
 *     "no lines"); an absent or unreadable list is null. Surfaces combine a
 *     false with any legacy warning they still render.
 * Confirmed acknowledgements are kept and merged with each new status read by
 * id (acknowledgements are never deleted), so a stale poll that lands after the
 * answer cannot un-acknowledge a line, and a reordered read cannot move one.
 *
 * PER RUN. Everything learned here is tagged with the run it was learned on and
 * read back only for that run. Line ids repeat across runs (`early_exit`,
 * `metadata_warning`, `service_summary_warning`), and a host whose run id and
 * status change in the SAME render (the run record route) would otherwise show
 * the previous run's acknowledgement on the new run — a false "all
 * acknowledged". There is no reset effect, so no committed frame pairs the new
 * run with the old run's answers (the trap `useInstancePush` documents).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  ACKNOWLEDGE_FAILED,
  mergeAcknowledgements,
  needsAcknowledgementOf,
  readWarningItems,
  type AcknowledgeWarningResult,
  type RunWarningItem,
} from "../warningAcknowledgement";

export type AcknowledgeWarningFn = (
  instanceId: string,
  warningId: string,
) => Promise<AcknowledgeWarningResult>;

/** The sentence of a refused view-as action (`code === "viewing_as"`), else null. */
function viewAsRefusalMessage(err: unknown): string | null {
  if (typeof err !== "object" || err === null) return null;
  const { code, message } = err as { code?: unknown; message?: unknown };
  return code === "viewing_as" && typeof message === "string" && message.trim() !== "" ? message : null;
}

export interface WarningAckState {
  /** Confirmed lines; null = the payload predates acknowledgement (old banners). */
  items: RunWarningItem[] | null;
  /** Re-derived from `items`; false for `[]` (server-confirmed no lines), null when the list is absent or unreadable (the pill keeps today's look). Never copied. */
  needsAcknowledgement: boolean | null;
  canAcknowledge: boolean;
  pendingIds: ReadonlySet<string>;
  errorById: Readonly<Record<string, string>>;
  /**
   * Line ids THIS screen's own press confirmed (the answer had `already_acknowledged`
   * false), for the current run. The API publishes names, never OIDs, so this is the one
   * proof that "you" did it; a line somebody else got to first is never in here.
   */
  ownIds: ReadonlySet<string>;
  acknowledge: (warningId: string) => Promise<void>;
}

/** What THIS screen learned about ONE run. */
interface RunSlot {
  iid: string | null;
  confirmed: readonly RunWarningItem[];
  pending: ReadonlySet<string>;
  errors: Readonly<Record<string, string>>;
  own: ReadonlySet<string>;
}

const NONE: ReadonlySet<string> = new Set();
const NO_ITEMS: readonly RunWarningItem[] = [];
const NO_ERRORS: Readonly<Record<string, string>> = {};

function emptySlot(iid: string | null): RunSlot {
  return { iid, confirmed: NO_ITEMS, pending: NONE, errors: NO_ERRORS, own: NONE };
}

/** Every line this answer confirms — its list, plus the pressed line's top-level record. */
function confirmedBy(
  result: AcknowledgeWarningResult,
  warningId: string,
  instanceId: string,
): RunWarningItem[] {
  // An answer about another run confirms nothing here.
  if (result.instance_id && result.instance_id.toLowerCase() !== instanceId.toLowerCase()) return [];
  // Strict parse (acknowledged === true only); a null read confirms nothing (fail safe).
  const listed = (readWarningItems({ warning_items: result.warning_items }) ?? []).filter(
    (i) => i.acknowledged,
  );
  if (
    listed.some((i) => i.id === warningId) ||
    result.warning_id !== warningId ||
    !result.acknowledged_by_name
  ) {
    return listed;
  }
  return [
    ...listed,
    {
      id: warningId,
      kind: "",
      message: "",
      acknowledged: true,
      acknowledged_by_name: result.acknowledged_by_name,
      acknowledged_at: result.acknowledged_at ?? null,
    },
  ];
}

export function useWarningAcknowledgements({
  instanceId,
  status,
  acknowledge,
}: {
  instanceId: string | null | undefined;
  status: unknown;
  acknowledge: AcknowledgeWarningFn;
}): WarningAckState {
  // Run ids compare case-insensitively (a GUID URL may be upper-case; the API answers lower-case).
  const rawIid = instanceId ?? null;
  const iidNow = rawIid === null ? null : rawIid.toLowerCase();
  const serverItems = useMemo(() => readWarningItems(status), [status]);
  const canAcknowledge =
    !!status &&
    typeof status === "object" &&
    (status as { can_acknowledge?: unknown }).can_acknowledge === true;

  // One slot PER RUN, so a run left and returned to reads its own settled state.
  const [slots, setSlots] = useState<Readonly<Record<string, RunSlot>>>({});
  // Another run's slot is never read back — not even in the render where the run changes.
  const here = (iidNow !== null ? slots[iidNow] : undefined) ?? emptySlot(iidNow);

  // Presses in flight for the current run, read synchronously so a double press sends one request.
  const inFlightRef = useRef<{ iid: string | null; ids: Set<string> }>({ iid: iidNow, ids: new Set() });
  // Kept current during render (as useInstancePush does) so a late answer can tell the run changed.
  const instanceRef = useRef(iidNow);
  instanceRef.current = iidNow;
  const rawIidRef = useRef(rawIid);
  rawIidRef.current = rawIid;
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const items = useMemo(
    () => (serverItems ? mergeAcknowledgements(serverItems, here.confirmed) : null),
    [serverItems, here.confirmed],
  );
  // R50: re-derived from the merged lines, never copied off the payload.
  const needsAcknowledgement = items ? needsAcknowledgementOf(items) : null;

  const acknowledgeLine = useCallback(
    async (warningId: string) => {
      const iid = instanceRef.current;
      if (!iid) return;
      const rawId = rawIidRef.current ?? iid;
      if (inFlightRef.current.iid !== iid) inFlightRef.current = { iid, ids: new Set() };
      const inFlight = inFlightRef.current.ids;
      if (inFlight.has(warningId)) return;
      inFlight.add(warningId);
      // Every write lands on the PRESSED run's own slot, wherever the screen is now.
      // A press on A, then B, then back to A can resend A's request; the server is idempotent.
      const update = (change: (s: RunSlot) => RunSlot) => {
        if (!mountedRef.current) return;
        setSlots((prev) => ({ ...prev, [iid]: change(prev[iid] ?? emptySlot(iid)) }));
      };
      update((s) => {
        const errors = { ...s.errors };
        delete errors[warningId];
        return { ...s, pending: new Set([...s.pending, warningId]), errors };
      });
      try {
        const result = await acknowledge(rawId, warningId);
        const confirmed = confirmedBy(result, warningId, iid);
        const confirmsPressed = confirmed.some((i) => i.id === warningId);
        update((s) => {
          // The first confirmation of a line stands: the server keeps the first acknowledger.
          const byId = new Map(s.confirmed.map((i) => [i.id, i] as const));
          for (const item of confirmed) {
            if (!byId.has(item.id)) byId.set(item.id, item);
          }
          // An answer that does not confirm the pressed line is a failure, never a silent revert.
          const errors = confirmsPressed ? s.errors : { ...s.errors, [warningId]: ACKNOWLEDGE_FAILED };
          // Own only when the server says this press was the first one for the pressed line.
          const own =
            confirmsPressed && result.already_acknowledged === false ? new Set([...s.own, warningId]) : s.own;
          return { ...s, confirmed: [...byId.values()], errors, own };
        });
      } catch (err) {
        // A view-as refusal says its own sentence: a retry fails the same way, so never "Try again".
        const message = viewAsRefusalMessage(err) ?? ACKNOWLEDGE_FAILED;
        update((s) => ({ ...s, errors: { ...s.errors, [warningId]: message } }));
      } finally {
        inFlight.delete(warningId);
        update((s) => {
          const pending = new Set(s.pending);
          pending.delete(warningId);
          return { ...s, pending };
        });
      }
    },
    [acknowledge],
  );

  // Memoized so consumers' own useMemo deps on this object stay stable.
  return useMemo(
    () => ({
      items,
      needsAcknowledgement,
      canAcknowledge,
      pendingIds: here.pending,
      errorById: here.errors,
      ownIds: here.own,
      acknowledge: acknowledgeLine,
    }),
    [items, needsAcknowledgement, canAcknowledge, here.pending, here.errors, here.own, acknowledgeLine],
  );
}

export default useWarningAcknowledgements;
